import { ReportingPageContext } from "@/pages/reporting_v2/context";
import { REPORTING_TAB_TYPES } from "@/pages/reporting_v2/types";

import { useContext, useLayoutEffect, useMemo, useRef, useState } from "react";

import { sumBy } from "lodash";

import type { ReportingTableHeader, TableRow } from "../../types";
import { ReportingTableBodyRow } from "./body/bodyRow";
import { ReportingTableHeaderCell } from "./header/headerCell";
import { ReportingTableTotalsRow } from "./totals/totalsRow";
import { getStickyPositions } from "./utils";

type Props = {
  headers: ReportingTableHeader[];
  rows: TableRow[];
  totalsRow: TableRow;
  selectedRows: Record<string, boolean>;
  onToggleRow: (key: string) => void;
};

// Matches the bottom padding of the reporting page plus the table card border.
const PAGE_BOTTOM_OFFSET = 45;
const MIN_TABLE_HEIGHT = 300;

export const ReportingTable: React.FC<Props> = ({
  headers,
  rows,
  totalsRow,
  selectedRows,
  onToggleRow,
}) => {
  const { tabType } = useContext(ReportingPageContext);

  const isIndividualTab = tabType === REPORTING_TAB_TYPES.INDIVIDUAL;

  const minWidth = useMemo(() => sumBy(headers, "width"), [headers]);
  const stickyPositions = useMemo(() => getStickyPositions(headers), [headers]);

  const containerRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<number>();

  useLayoutEffect(() => {
    const updateMaxHeight = () => {
      const container = containerRef.current;
      if (!container) return;
      const top = container.getBoundingClientRect().top + window.scrollY;
      const footerHeight =
        (container.nextElementSibling as HTMLElement | null)?.offsetHeight ?? 0;
      setMaxHeight(
        Math.max(
          MIN_TABLE_HEIGHT,
          window.innerHeight - top - footerHeight - PAGE_BOTTOM_OFFSET,
        ),
      );
    };
    updateMaxHeight();
    window.addEventListener("resize", updateMaxHeight);
    return () => window.removeEventListener("resize", updateMaxHeight);
  }, [tabType]);

  return (
    <div
      ref={containerRef}
      className="scrollbar-v2 overflow-auto overscroll-none"
      style={{ maxHeight }}
    >
      <table
        className="w-full table-fixed border-collapse"
        style={{ minWidth }}
      >
        <thead>
          <tr>
            {headers.map((header) => (
              <ReportingTableHeaderCell
                key={header.id}
                header={header}
                stickyPosition={stickyPositions[header.id]}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <ReportingTableBodyRow
              key={row.key}
              headers={headers}
              row={row}
              stickyPositions={stickyPositions}
              isIndividualTab={isIndividualTab}
              isSelected={!!selectedRows[row.key]}
              onToggleRow={onToggleRow}
            />
          ))}
        </tbody>
        <tfoot>
          <ReportingTableTotalsRow
            headers={headers}
            totalsRow={totalsRow}
            stickyPositions={stickyPositions}
          />
        </tfoot>
      </table>
    </div>
  );
};
